import React, { useState } from 'react';
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  InputNumber,
  DatePicker,
  Select,
  Space,
  Tag,
  message,
  Row,
  Col,
  Statistic,
  Progress,
  Timeline,
  Typography,
  Tabs,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  PlusOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  TrophyOutlined,
  LineChartOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;
const { TabPane } = Tabs;

// Mock data - will be replaced with actual API calls
const mockMeasurements = [
  {
    id: '1',
    kpi_name: 'First Pass Yield',
    measurement_date: '2025-12-06',
    target_value: 95.0,
    actual_value: 96.5,
    variance: 1.5,
    variance_percentage: 1.58,
    status: 'Achieved',
    achievement_level: 'Excellent',
    measured_by: 'John Doe',
    verified_by: 'Jane Smith',
  },
  {
    id: '2',
    kpi_name: 'Defect Rate',
    measurement_date: '2025-12-05',
    target_value: 5.0,
    actual_value: 6.2,
    variance: -1.2,
    variance_percentage: -24.0,
    status: 'Missed',
    achievement_level: 'Below Target',
    measured_by: 'John Doe',
    verified_by: null,
  },
  {
    id: '3',
    kpi_name: 'First Pass Yield',
    measurement_date: '2025-12-05',
    target_value: 95.0,
    actual_value: 94.5,
    variance: -0.5,
    variance_percentage: -0.53,
    status: 'Missed',
    achievement_level: 'Good',
    measured_by: 'John Doe',
    verified_by: 'Jane Smith',
  },
];

interface KPIMeasurement {
  id?: string;
  quality_plan_kpi_id: string;
  measurement_date: string;
  measurement_period?: string;
  actual_value: number;
  target_value: number;
  variance?: number;
  variance_percentage?: number;
  status?: string;
  achievement_level?: string;
  notes?: string;
  corrective_action?: string;
  root_cause?: string;
  measured_by?: string;
}

const KPIMeasurementsPage: React.FC = () => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [measurements, setMeasurements] = useState(mockMeasurements);

  const handleCreate = () => {
    form.resetFields();
    setIsModalVisible(true);
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      // TODO: Replace with actual API call
      // const newMeasurement = await recordKPIMeasurement(values);
      
      // Calculate variance
      const variance = values.actual_value - values.target_value;
      const variance_percentage = ((variance / values.target_value) * 100).toFixed(2);
      
      const newMeasurement = {
        ...values,
        id: Date.now().toString(),
        variance,
        variance_percentage: parseFloat(variance_percentage),
        status: variance >= 0 ? 'Achieved' : 'Missed',
        achievement_level: variance >= 0 ? 'Excellent' : 'Below Target',
      };

      setMeasurements([newMeasurement, ...measurements]);
      message.success('Measurement recorded successfully');
      setIsModalVisible(false);
      form.resetFields();
    } catch (error) {
      message.error('Failed to record measurement');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    return status === 'Achieved' ? 'success' : status === 'Missed' ? 'error' : 'warning';
  };

  const getAchievementColor = (level: string) => {
    switch (level) {
      case 'Excellent': return 'gold';
      case 'Good': return 'green';
      case 'Acceptable': return 'blue';
      case 'Below Target': return 'orange';
      case 'Critical': return 'red';
      default: return 'default';
    }
  };

  const columns: ColumnsType<any> = [
    {
      title: 'Date',
      dataIndex: 'measurement_date',
      key: 'measurement_date',
      width: 120,
      render: (date) => dayjs(date).format('DD MMM YYYY'),
    },
    {
      title: 'KPI Name',
      dataIndex: 'kpi_name',
      key: 'kpi_name',
      width: 200,
    },
    {
      title: 'Target',
      dataIndex: 'target_value',
      key: 'target_value',
      width: 100,
      align: 'right',
      render: (value) => value.toFixed(2),
    },
    {
      title: 'Actual',
      dataIndex: 'actual_value',
      key: 'actual_value',
      width: 100,
      align: 'right',
      render: (value) => value.toFixed(2),
    },
    {
      title: 'Variance',
      dataIndex: 'variance',
      key: 'variance',
      width: 120,
      align: 'right',
      render: (variance, record) => (
        <Space>
          <Text type={variance >= 0 ? 'success' : 'danger'}>
            {variance >= 0 ? '+' : ''}{variance.toFixed(2)}
          </Text>
          <Text type="secondary">
            ({record.variance_percentage >= 0 ? '+' : ''}{record.variance_percentage.toFixed(1)}%)
          </Text>
        </Space>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status) => (
        <Tag
          icon={status === 'Achieved' ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
          color={getStatusColor(status)}
        >
          {status}
        </Tag>
      ),
    },
    {
      title: 'Achievement',
      dataIndex: 'achievement_level',
      key: 'achievement_level',
      width: 130,
      render: (level) => (
        <Tag color={getAchievementColor(level)}>{level}</Tag>
      ),
    },
    {
      title: 'Measured By',
      dataIndex: 'measured_by',
      key: 'measured_by',
      width: 150,
    },
    {
      title: 'Verified',
      dataIndex: 'verified_by',
      key: 'verified_by',
      width: 120,
      render: (verified) => verified ? (
        <Tag icon={<CheckCircleOutlined />} color="success">Verified</Tag>
      ) : (
        <Tag color="warning">Pending</Tag>
      ),
    },
  ];

  // Calculate statistics
  const stats = {
    total: measurements.length,
    achieved: measurements.filter(m => m.status === 'Achieved').length,
    missed: measurements.filter(m => m.status === 'Missed').length,
    avgAchievement: measurements.length > 0 
      ? (measurements.filter(m => m.status === 'Achieved').length / measurements.length * 100).toFixed(1)
      : 0,
  };

  return (
    <div style={{ padding: 24 }}>
      {/* Header */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col span={24}>
          <Card>
            <Row justify="space-between" align="middle">
              <Col>
                <Title level={3} style={{ margin: 0 }}>
                  KPI Measurements
                </Title>
                <Text type="secondary">
                  Record and track KPI performance measurements
                </Text>
              </Col>
              <Col>
                <Space>
                  <Button
                    icon={<LineChartOutlined />}
                    size="large"
                  >
                    View Trends
                  </Button>
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={handleCreate}
                    size="large"
                  >
                    Record Measurement
                  </Button>
                </Space>
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>

      {/* Statistics */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Total Measurements"
              value={stats.total}
              prefix={<TrophyOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Achieved"
              value={stats.achieved}
              valueStyle={{ color: '#3f8600' }}
              prefix={<CheckCircleOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Missed"
              value={stats.missed}
              valueStyle={{ color: '#cf1322' }}
              prefix={<WarningOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Achievement Rate"
              value={stats.avgAchievement}
              suffix="%"
              valueStyle={{ color: Number(stats.avgAchievement) >= 80 ? '#3f8600' : '#cf1322' }}
            />
            <Progress
              percent={Number(stats.avgAchievement)}
              strokeColor={Number(stats.avgAchievement) >= 80 ? '#3f8600' : '#cf1322'}
              showInfo={false}
            />
          </Card>
        </Col>
      </Row>

      {/* Content */}
      <Card>
        <Tabs defaultActiveKey="measurements">
          <TabPane tab="All Measurements" key="measurements">
            <Table
              columns={columns}
              dataSource={measurements}
              rowKey="id"
              pagination={{
                showSizeChanger: true,
                showTotal: (total) => `Total ${total} measurements`,
              }}
            />
          </TabPane>
          <TabPane tab="Recent Activity" key="activity">
            <Timeline
              items={measurements.slice(0, 5).map(m => ({
                color: m.status === 'Achieved' ? 'green' : 'red',
                children: (
                  <div>
                    <div><strong>{m.kpi_name}</strong></div>
                    <div>
                      <Text type="secondary">{dayjs(m.measurement_date).format('DD MMM YYYY')}</Text>
                      {' - '}
                      <Text type={m.variance >= 0 ? 'success' : 'danger'}>
                        {m.actual_value.toFixed(2)} / {m.target_value.toFixed(2)}
                      </Text>
                      {' '}
                      <Tag color={getStatusColor(m.status)}>{m.status}</Tag>
                    </div>
                  </div>
                ),
              }))}
            />
          </TabPane>
        </Tabs>
      </Card>

      {/* Record Measurement Modal */}
      <Modal
        title="Record KPI Measurement"
        open={isModalVisible}
        onOk={handleSubmit}
        onCancel={() => {
          setIsModalVisible(false);
          form.resetFields();
        }}
        width={700}
        confirmLoading={loading}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="quality_plan_kpi_id"
            label="Select KPI"
            rules={[{ required: true, message: 'Please select a KPI' }]}
          >
            <Select
              placeholder="Select KPI to measure"
              showSearch
              optionFilterProp="children"
            >
              <Option value="kpi-1">First Pass Yield (Target: 95%)</Option>
              <Option value="kpi-2">Defect Rate (Target: 5 defects/1000)</Option>
              <Option value="kpi-3">On-Time Delivery (Target: 98%)</Option>
              <Option value="kpi-4">Rework Rate (Target: 2%)</Option>
            </Select>
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="measurement_date"
                label="Measurement Date"
                rules={[{ required: true, message: 'Please select date' }]}
              >
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="measurement_period" label="Period (Optional)">
                <Input placeholder="e.g., Week 45, Nov 2025" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="target_value"
                label="Target Value"
                rules={[{ required: true, message: 'Please enter target value' }]}
              >
                <InputNumber
                  style={{ width: '100%' }}
                  placeholder="95.0"
                  step={0.1}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="actual_value"
                label="Actual Value"
                rules={[{ required: true, message: 'Please enter actual value' }]}
              >
                <InputNumber
                  style={{ width: '100%' }}
                  placeholder="96.5"
                  step={0.1}
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="notes" label="Notes">
            <TextArea rows={3} placeholder="Additional observations or comments" />
          </Form.Item>

          <Form.Item name="corrective_action" label="Corrective Action (if missed)">
            <TextArea rows={2} placeholder="Actions taken to improve performance" />
          </Form.Item>

          <Form.Item name="root_cause" label="Root Cause (if missed)">
            <TextArea rows={2} placeholder="Why target was missed" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default KPIMeasurementsPage;

